"use client";

import * as React from "react";
import { Slot as SlotPrimitive } from "radix-ui";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";
import { TONE_SURFACE } from "@/components/ui/tone";

const alertBannerVariants = cva(
  "flex flex-col gap-3 text-sm sm:flex-row sm:items-center sm:justify-between sm:gap-4",
  {
    variants: {
      variant: {
        default: TONE_SURFACE.neutral,
        warning: TONE_SURFACE.warning,
        critical: TONE_SURFACE.destructive,
        info: TONE_SURFACE.info,
        success: TONE_SURFACE.success,
      },
      layout: {
        strip: "px-4 py-2.5 border-b",
        card: "px-3 py-3 rounded-sm border sm:px-4",
      },
    },
    defaultVariants: {
      variant: "default",
      layout: "strip",
    },
  },
);

interface AlertBannerContextValue {
  variant: VariantProps<typeof alertBannerVariants>["variant"];
}

const AlertBannerContext = React.createContext<AlertBannerContextValue>({
  variant: "default",
});

interface AlertBannerProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof alertBannerVariants> {
  asChild?: boolean;
}

function AlertBanner({
  className,
  variant,
  layout,
  asChild = false,
  children,
  ...props
}: AlertBannerProps) {
  const Comp = asChild ? SlotPrimitive.Slot : "div";

  return (
    <AlertBannerContext value={{ variant }}>
      <Comp
        className={cn(alertBannerVariants({ variant, layout }), className)}
        {...props}
      >
        {children}
      </Comp>
    </AlertBannerContext>
  );
}

type AlertBannerContentProps = React.HTMLAttributes<HTMLDivElement>;

function AlertBannerContent({
  className,
  children,
  ...props
}: AlertBannerContentProps) {
  return (
    <div
      className={cn("flex items-start gap-3 sm:items-center", className)}
      {...props}
    >
      {children}
    </div>
  );
}

interface AlertBannerIconProps extends React.HTMLAttributes<HTMLDivElement> {
  boxed?: boolean;
}

function AlertBannerIcon({
  className,
  boxed = false,
  children,
  ...props
}: AlertBannerIconProps) {
  const { variant } = React.use(AlertBannerContext);

  if (boxed) {
    return (
      <div
        className={cn(
          "flex items-center justify-center size-8 rounded-md shrink-0",
          variant === "info" && "bg-info text-info-foreground",
          variant === "warning" && "bg-warning text-warning-foreground",
          variant === "critical" &&
            "bg-destructive text-destructive-foreground",
          variant === "success" && "bg-success text-success-foreground",
          variant === "default" && "border border-border bg-card text-muted-foreground",
          className,
        )}
        {...props}
      >
        {children}
      </div>
    );
  }

  return (
    <span className={cn("shrink-0 [&>svg]:size-4", className)} {...props}>
      {children}
    </span>
  );
}

type AlertBannerTextProps = React.HTMLAttributes<HTMLDivElement>;

function AlertBannerText({
  className,
  children,
  ...props
}: AlertBannerTextProps) {
  return (
    <div className={cn("flex flex-col", className)} {...props}>
      {children}
    </div>
  );
}

type AlertBannerTitleProps = React.HTMLAttributes<HTMLSpanElement>;

function AlertBannerTitle({
  className,
  children,
  ...props
}: AlertBannerTitleProps) {
  return (
    <span className={cn("font-medium", className)} {...props}>
      {children}
    </span>
  );
}

type AlertBannerDescriptionProps = React.HTMLAttributes<HTMLSpanElement>;

function AlertBannerDescription({
  className,
  children,
  ...props
}: AlertBannerDescriptionProps) {
  return (
    <span className={cn("text-xs text-muted-foreground", className)} {...props}>
      {children}
    </span>
  );
}

type AlertBannerActionProps = React.HTMLAttributes<HTMLDivElement>;

function AlertBannerAction({
  className,
  children,
  ...props
}: AlertBannerActionProps) {
  return (
    <div className={cn("shrink-0", className)} {...props}>
      {children}
    </div>
  );
}

export {
  AlertBanner,
  AlertBannerContent,
  AlertBannerIcon,
  AlertBannerText,
  AlertBannerTitle,
  AlertBannerDescription,
  AlertBannerAction,
  alertBannerVariants,
};
